import type {
  EditorValidity,
  MessageContent,
  MessageDiagnostic,
} from "../types"
import { parseMessageContent } from "./conversion"
import { diagnostic, entityDiagnostics, validUrl } from "./shared"
import { isUrlButtonStyle } from "../buttons/urlButtonStyle"

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Pure local structural validation. No length/button/slot caps, substitutions,
 * transport calls or promises of Telegram acceptance. The backend validates
 * final rendered length and media readiness; the UI owns last-good previews. */
export function validateMessageContent(
  content: MessageContent,
): EditorValidity {
  const diagnostics: MessageDiagnostic[] = []
  const parsed = parseMessageContent(content)
  if (!parsed.ok) diagnostics.push(...parsed.diagnostics)
  else if (content.formatting.kind === "entities")
    diagnostics.push(
      ...entityDiagnostics(parsed.value.text, parsed.value.entities).filter(
        (item) => item.severity === "warning",
      ),
    )

  if (!Array.isArray(content.mediaIds))
    diagnostics.push(
      diagnostic("INVALID_MEDIA", "mediaIds must be an array", "/mediaIds"),
    )
  else {
    content.mediaIds.forEach((id, index) => {
      if (typeof id !== "string" || !uuid.test(id))
        diagnostics.push(
          diagnostic(
            "INVALID_MEDIA_ID",
            "Media ID must be a UUID",
            `/mediaIds/${index}`,
          ),
        )
    })
    if (content.mediaIds.length > 10)
      diagnostics.push(
        diagnostic(
          "INVALID_ALBUM_SIZE",
          "A media album contains 2–10 items",
          "/mediaIds",
        ),
      )
    if (!content.mediaIds.length && parsed.ok && !parsed.value.text.length)
      diagnostics.push(diagnostic("EMPTY_CONTENT", "Text or media is required"))
  }
  if (!Array.isArray(content.buttons))
    diagnostics.push(
      diagnostic(
        "INVALID_KEYBOARD",
        "buttons must be an array of rows",
        "/buttons",
      ),
    )
  else {
    if (
      Array.isArray(content.mediaIds) &&
      content.mediaIds.length > 1 &&
      content.buttons.length
    )
      diagnostics.push(
        diagnostic(
          "ALBUM_KEYBOARD",
          "Albums cannot have a keyboard",
          "/buttons",
        ),
      )
    content.buttons.forEach((row, rowIndex) => {
      const path = `/buttons/${rowIndex}`
      if (!Array.isArray(row) || !row.length) {
        diagnostics.push(
          diagnostic(
            "INVALID_BUTTON_ROW",
            "Button rows must be nonempty arrays",
            path,
          ),
        )
        return
      }
      row.forEach((button, buttonIndex) => {
        const buttonPath = `${path}/${buttonIndex}`
        if (
          !button ||
          typeof button !== "object" ||
          Object.keys(button).some(
            (key) => !["text", "url", "style"].includes(key),
          )
        ) {
          diagnostics.push(
            diagnostic(
              "INVALID_BUTTON",
              "Only text, url and style are supported",
              buttonPath,
            ),
          )
          return
        }
        if (button.style !== undefined && !isUrlButtonStyle(button.style))
          diagnostics.push(
            diagnostic(
              "INVALID_BUTTON_STYLE",
              "Button style must be primary, success or danger, or omitted",
              `${buttonPath}/style`,
            ),
          )
        if (typeof button.text !== "string" || !button.text.length)
          diagnostics.push(
            diagnostic(
              "INVALID_BUTTON_TEXT",
              "Button text must be nonempty",
              `${buttonPath}/text`,
            ),
          )
        if (!validUrl(button.url))
          diagnostics.push(
            diagnostic(
              "INVALID_URL",
              "Use HTTP(S) or a whole slot marker",
              `${buttonPath}/url`,
            ),
          )
      })
    })
  }
  const valid = !diagnostics.some((item) => item.severity === "error")
  // Submit constraints (an empty draft, an album keyboard, etc.) do not make
  // parsed text stale or prevent the user from editing it.
  return { valid, diagnostics, previewStale: !parsed.ok }
}
